import { DimensionOut } from '@/api/generated/schemas';
import type { Dimension, IndustryBenchmark, Research, ResearchSummary } from '@/api/types';

import type { RadarAxis } from '../DimensionRadar';

export interface ScoredDimension {
  dimension: Dimension;
  value: number | null;
  contribution: number | null;
  addedSince: boolean;
}

/** The dimensions the run's prompt carried (the contract types them as free objects); null for older runs. */
function recordedDimensions(research: Research): Dimension[] | null {
  const parsed = DimensionOut.array().safeParse(research.dimensions);
  return parsed.success ? parsed.data : null;
}

/** What a run's scorecard needs per current dimension: its score and the points it adds to the total. */
export function scoredDimensions(research: Research, dimensions: Dimension[]): ScoredDimension[] {
  // The total is the weighted average over the current dimensions this run
  // scored, so each one adds score × weight / (weights of the scored ones).
  const recorded = recordedDimensions(research);
  const scoredWeight = dimensions.reduce((sum, d) => sum + (research.scores[d.key] != null ? d.weight : 0), 0);
  return dimensions.map((d) => {
    const value = research.scores[d.key] ?? null;
    const contribution = value === null || !scoredWeight ? null : (value * d.weight) / scoredWeight;
    const addedSince = value === null && recorded !== null && !recorded.some((s) => s.key === d.key);
    return { dimension: d, value, contribution, addedSince };
  });
}

/** Dimensions retired since the run: still part of its record, no longer of the total. */
export function retiredDimensions(research: Research, dimensions: Dimension[]): Dimension[] {
  const current = new Set(dimensions.map((d) => d.key));
  const recorded = recordedDimensions(research) ?? Object.keys(research.scores).map((key) => ({ key, label: key, description: '', weight: 0 }));
  return recorded.filter((d) => !current.has(d.key) && research.scores[d.key] != null);
}

/** The radar axes for a run: each scored current dimension with its weight, points and the industry average. */
export function radarAxes(research: Research, dimensions: Dimension[], benchmark: IndustryBenchmark | null): RadarAxis[] {
  return scoredDimensions(research, dimensions).flatMap(({ dimension: d, value, contribution }) =>
    value === null ? [] : [{ key: d.key, label: d.label, value, weight: d.weight, points: contribution, benchmark: benchmark?.scores[d.key] ?? null }],
  );
}

/** How long research took, from the worker picking the run up to its end: "42s", "1m 24s". */
export function runDuration(run: ResearchSummary): string | null {
  if (!run.started_at || !run.finished_at) return null;
  const seconds = Math.max(0, Math.round((Date.parse(run.finished_at) - Date.parse(run.started_at)) / 1000));
  return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, '0')}s`;
}
